import { dropSuperseded } from '@src/editor/dubbing/DubPlacementService';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDubManifestFetch } from './useDubbedLanguages';

import type { DubManifestSegment } from '@src/repositories/DubbingRepository';

const logger = LogManager.getLogger('aiVoices:useDubPassagePlayback');

export type DubPassagePlaybackState =
  | 'idle'
  | 'loading'
  | 'playing'
  /** The dub has no recording for these lines (or none that could be read). */
  | 'unavailable';

/**
 * PLAT-12022 — play the dub's CURRENT take of a few lines, on their own.
 *
 * The whole-story player (`composeDubTrack`) decodes every rendition and lays
 * each line at its place on the story timeline, gaps and all. A person about
 * to re-record a passage wants to hear just that passage, back to back: this
 * reads the dub manifest, keeps the live recording of each asked-for line,
 * decodes the rendition units those lines are sliced from, and plays the
 * slices one after another through a short `AudioBuffer`.
 *
 * Lazy: nothing is fetched until the first `toggle()`. Decoded once per
 * (story, language, lines); a second press replays. Stops on unmount, so a
 * passage cannot keep playing after its popover is gone.
 */
export function useDubPassagePlayback(params: {
  storyId: string;
  language: string;
  segmentIds: readonly string[];
  /**
   * PLAT-12080 — bumped when a new take of these lines lands while the
   * player is still mounted, so the next press reads the dub again instead
   * of replaying the buffer it cached from the take before.
   */
  reloadToken?: number;
}): { state: DubPassagePlaybackState; toggle: () => void } {
  const { storyId, language, reloadToken = 0 } = params;
  const segmentIdsKey = params.segmentIds.join(',');
  const fetchManifest = useDubManifestFetch();
  const [state, setState] = useState<DubPassagePlaybackState>('idle');
  const bufferRef = useRef<AudioBuffer | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    try {
      sourceRef.current?.stop();
    } catch {
      // Already stopped.
    }
    sourceRef.current = null;
  }, []);

  // A different passage is a different buffer; so is a new take of it.
  useEffect(() => {
    stop();
    abortRef.current?.abort();
    bufferRef.current = null;
    setState('idle');
  }, [storyId, language, segmentIdsKey, reloadToken, stop]);

  useEffect(
    () => () => {
      stop();
      abortRef.current?.abort();
      void ctxRef.current?.close();
      ctxRef.current = null;
    },
    [stop],
  );

  const play = useCallback(
    (buffer: AudioBuffer) => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      stop();
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.onended = () => {
        if (sourceRef.current === source) {
          sourceRef.current = null;
          setState('idle');
        }
      };
      sourceRef.current = source;
      source.start();
      setState('playing');
    },
    [stop],
  );

  const toggle = useCallback(() => {
    if (state === 'playing') {
      stop();
      setState('idle');
      return;
    }
    if (state === 'loading' || state === 'unavailable') return;
    if (bufferRef.current) {
      play(bufferRef.current);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setState('loading');
    void (async () => {
      try {
        const ctx = (ctxRef.current ??= new AudioContext());
        const resolved = await fetchManifest({ storyId, language });
        if (controller.signal.aborted) return;
        const wanted = new Set(segmentIdsKey.split(','));
        const lines = resolved
          ? dropSuperseded(resolved.manifest.segments ?? []).live.filter(s =>
              wanted.has(s.segmentId),
            )
          : [];
        const buffer = resolved
          ? await slicePassage(
              ctx,
              lines,
              resolved.renditionUrls,
              controller.signal,
            )
          : null;
        if (controller.signal.aborted) return;
        if (!buffer) {
          setState('unavailable');
          return;
        }
        bufferRef.current = buffer;
        play(buffer);
      } catch (err) {
        if (isAbortError(err) || controller.signal.aborted) return;
        logger.warn('Passage playback failed', extractErrorInfo(err));
        setState('unavailable');
      }
    })();
  }, [state, stop, play, fetchManifest, storyId, language, segmentIdsKey]);

  return { state, toggle };
}

/**
 * The asked-for lines' slices, back to back in source order, as one buffer.
 * `null` when none of them could be read.
 */
async function slicePassage(
  ctx: AudioContext,
  lines: readonly DubManifestSegment[],
  renditionUrls: Record<string, string>,
  signal: AbortSignal,
): Promise<AudioBuffer | null> {
  if (lines.length === 0) return null;
  const units = new Map<string, AudioBuffer>();
  await Promise.all(
    Array.from(new Set(lines.map(l => l.renditionId))).map(async id => {
      const url = renditionUrls[id];
      if (!url) {
        logger.warn('Rendition has no signed URL', { renditionId: id });
        return;
      }
      const res = await fetch(url, { signal });
      if (!res.ok) {
        logger.warn('Rendition fetch not ok', {
          renditionId: id,
          status: res.status,
        });
        return;
      }
      units.set(id, await ctx.decodeAudioData(await res.arrayBuffer()));
    }),
  );
  const playable = lines.filter(l => units.has(l.renditionId));
  if (playable.length === 0) return null;
  const { sampleRate } = ctx;
  const frames = playable.reduce(
    (n, l) => n + Math.floor((l.renditionDurationMs / 1000) * sampleRate),
    0,
  );
  const out = ctx.createBuffer(1, Math.max(1, frames), sampleRate);
  const data = out.getChannelData(0);
  let at = 0;
  for (const line of playable) {
    const unit = units.get(line.renditionId)!;
    const src = unit.getChannelData(0);
    const start = Math.floor((line.renditionOffsetMs / 1000) * sampleRate);
    const len = Math.floor((line.renditionDurationMs / 1000) * sampleRate);
    for (
      let i = 0;
      i < len && start + i < src.length && at < data.length;
      i++
    ) {
      data[at++] = src[start + i];
    }
  }
  return out;
}
